import { ApiError, getAuthMethods, kerberosLoginUrl } from "@/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/themes/theme";
import { FormEvent, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
  const ssoFailed = new URLSearchParams(location.search).get("sso_error") === "1";

  const [principal, setPrincipal] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [kerberosEnabled, setKerberosEnabled] = useState(false);

  useEffect(() => {
    getAuthMethods()
      .then((methods) => setKerberosEnabled(methods.kerberos))
      .catch(() => setKerberosEnabled(false));
  }, []);

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(principal.trim(), password);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Anmeldung fehlgeschlagen.");
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Anmeldung fehlgeschlagen.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <div className="flex justify-end p-3">
        <Button variant="ghost" size="sm" onClick={toggleTheme}>
          {theme === "dark" ? "Hell" : "Dunkel"}
        </Button>
      </div>
      <div className="flex flex-1 items-start justify-center pt-[12vh]">
        <form
          onSubmit={(e) => void onSubmit(e)}
          className="w-full max-w-sm rounded-lg border border-hairline bg-surface p-5 shadow-sm"
        >
          <h1 className="text-lg font-semibold tracking-tight">Edicta</h1>
          <p className="mt-1 text-sm text-muted">Mit Operator-Konto anmelden.</p>
          <label className="field-label mt-4" htmlFor="principal">
            Benutzername
          </label>
          <input
            id="principal"
            className="field"
            autoComplete="username"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            required
          />
          <label className="field-label mt-3" htmlFor="password">
            Passwort
          </label>
          <input
            id="password"
            type="password"
            className="field"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {ssoFailed ? (
            <p className="mt-3 text-sm text-danger">
              Automatische Anmeldung fehlgeschlagen. Bitte mit Passwort anmelden.
            </p>
          ) : null}
          {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
          <Button type="submit" variant="primary" className="mt-4 w-full" disabled={submitting || !principal}>
            {submitting ? "Anmelden…" : "Anmelden"}
          </Button>
          {kerberosEnabled ? (
            <>
              <div className="my-4 flex items-center gap-3 text-xs text-muted">
                <span className="h-px flex-1 bg-hairline" />
                oder
                <span className="h-px flex-1 bg-hairline" />
              </div>
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => window.location.assign(kerberosLoginUrl(from))}
              >
                Mit Kerberos anmelden
              </Button>
            </>
          ) : null}
        </form>
      </div>
    </div>
  );
}
